import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import apiClient from '@/services/api-client';
import {
  getParameterSchema,
  getParameterList,
  getParameterById,
  createParameter,
  updateParameter,
  getCheckerParameterList,
  performCheckerAction,
  reviseParameter,
  getParameterCounts,
} from '@/services/tenant/parameter-service';
import {
  ApiResponse,
  ParametersByTable,
  ParameterTable,
} from '@/services/types';
import {
  ParameterSchema,
  ParameterListResponse,
  ParameterListParams,
  ParameterMutationPayload,
  CheckerListResponse,
  CheckerListParams,
  CheckerActionPayload,
  RevisionPayload,
  ParameterCountsResponse,
} from '@/services/types/parameter-types';
import { API_ENDPOINTS } from '@/utils/constants/end-points';

// ============================================================================
// STATE
// ============================================================================

interface ParametersState {
  // --- Existing (backward compatible) ---
  data: ParametersByTable;
  availableTables: ParameterTable[];
  dataAccessLevelTables: ParameterTable[];
  loading: boolean;
  tablesLoading: boolean;
  error: string | null;

  // --- New: Schema-driven ---
  schemas: Record<string, ParameterSchema>;
  listData: Record<string, ParameterListResponse>;
  selectedItem: Record<string, any> | null;
  checkerList: CheckerListResponse | null;
  counts: ParameterCountsResponse | null;
  /**
   * Per-module counts split by maker/checker side, so each option of the
   * status switch can carry its own number. `counts` above is their sum.
   */
  statusCounts: ParameterStatusCounts | null;

  // --- Operation states ---
  schemaLoading: boolean;
  listLoading: boolean;
  itemLoading: boolean;
  creating: boolean;
  updating: boolean;
  checkerLoading: boolean;
  actionLoading: boolean;
  countsLoading: boolean;
}

const initialState: ParametersState = {
  // Existing
  data: {},
  availableTables: [],
  dataAccessLevelTables: [],
  loading: false,
  tablesLoading: false,
  error: null,

  // New
  schemas: {},
  listData: {},
  selectedItem: null,
  checkerList: null,
  counts: null,
  statusCounts: null,

  // Operation states
  schemaLoading: false,
  listLoading: false,
  itemLoading: false,
  creating: false,
  updating: false,
  checkerLoading: false,
  actionLoading: false,
  countsLoading: false,
};

// A 200 whose body isn't valid JSON reaches us as a raw string (axios parses
// silently), so `res.data.data` is undefined. Reject instead of storing it:
// the sidebar and parameter pickers read these lists while rendering.
const MALFORMED_RESPONSE_MESSAGE = 'Unexpected response from the server';

/**
 * The table → values map, with a table that came back empty (`null`) as `[]`
 * so the pickers can still map it; undefined when the body is not a map of
 * lists at all.
 */
const toParametersByTable = (data: unknown): ParametersByTable | undefined => {
  if (typeof data !== 'object' || data === null || Array.isArray(data)) {
    return undefined;
  }
  const tables: ParametersByTable = {};
  for (const [table, values] of Object.entries(data)) {
    if (values === null || values === undefined) tables[table] = [];
    else if (Array.isArray(values)) tables[table] = values;
    else return undefined;
  }
  return tables;
};

export const fetchAvailableParameters = createAsyncThunk<
  ParameterTable[],
  void,
  { rejectValue: string }
>('parameters/fetchAvailable', async (_, { rejectWithValue }) => {
  try {
    const res = await apiClient.get<{
      success: boolean;
      data: ParameterTable[];
      message: string;
    }>(`${API_ENDPOINTS.PARAMETERS}`);
    if (!Array.isArray(res.data?.data)) {
      return rejectWithValue(MALFORMED_RESPONSE_MESSAGE);
    }
    return res.data.data;
  } catch (err: any) {
    return rejectWithValue(
      err?.message || 'Failed to fetch available parameters'
    );
  }
});

export const fetchDataAccessLevelParameters = createAsyncThunk<
  ParameterTable[],
  void,
  { rejectValue: string }
>('parameters/fetchDataAccessLevel', async (_, { rejectWithValue }) => {
  try {
    const res = await apiClient.get<{
      success: boolean;
      data: ParameterTable[];
      message: string;
    }>(`${API_ENDPOINTS.PARAMETERS}?isDataAccessLevel=true`);
    if (!Array.isArray(res.data?.data)) {
      return rejectWithValue(MALFORMED_RESPONSE_MESSAGE);
    }
    return res.data.data;
  } catch (err: any) {
    return rejectWithValue(
      err?.message || 'Failed to fetch data access level parameters'
    );
  }
});

export interface FetchParametersByTablesParams {
  tableNames: string[];
  isEdit?: boolean;
  module?: string;
}

export const fetchParametersByTables = createAsyncThunk<
  ParametersByTable,
  string[] | FetchParametersByTablesParams,
  { rejectValue: string }
>('parameters/fetchByTables', async (arg, { rejectWithValue }) => {
  try {
    const payload = Array.isArray(arg) ? { tableNames: arg } : arg;
    const res = await apiClient.post<ApiResponse<ParametersByTable>>(
      `${API_ENDPOINTS.PARAMETERS}/values`,
      payload
    );
    const data = toParametersByTable(res.data?.data);
    if (!data) {
      return rejectWithValue(MALFORMED_RESPONSE_MESSAGE);
    }
    return data;
  } catch (err: any) {
    return rejectWithValue(err?.message || 'Failed to fetch parameters');
  }
});

/** Fetch schema for a parameter table */
export const fetchParameterSchema = createAsyncThunk<
  ParameterSchema,
  string, // tableName
  { rejectValue: string }
>('parameters/fetchSchema', async (tableName, { rejectWithValue }) => {
  try {
    return await getParameterSchema(tableName);
  } catch (err: any) {
    return rejectWithValue(err?.message || 'Failed to fetch schema');
  }
});

/** Fetch paginated list for a parameter table */
export const fetchParameterList = createAsyncThunk<
  { tableName: string; response: ParameterListResponse },
  ParameterListParams,
  { rejectValue: string }
>('parameters/fetchList', async (params, { rejectWithValue }) => {
  try {
    const response = await getParameterList(params);
    return { tableName: params.tableName, response };
  } catch (err: any) {
    return rejectWithValue(err?.message || 'Failed to fetch list');
  }
});

/** Fetch a single parameter by ID */
export const fetchParameterById = createAsyncThunk<
  { tableName: string; data: Record<string, any> },
  { tableName: string; id: number },
  { rejectValue: string }
>('parameters/fetchById', async ({ tableName, id }, { rejectWithValue }) => {
  try {
    const data = await getParameterById(tableName, id);
    return { tableName, data };
  } catch (err: any) {
    return rejectWithValue(err?.message || 'Failed to fetch parameter');
  }
});

/** Create a new parameter */
export const createParameterThunk = createAsyncThunk<
  any,
  { tableName: string; payload: ParameterMutationPayload },
  { rejectValue: string }
>('parameters/create', async ({ tableName, payload }, { rejectWithValue }) => {
  try {
    return await createParameter(tableName, payload);
  } catch (err: any) {
    return rejectWithValue(err?.message || 'Failed to create parameter');
  }
});

/** Update a parameter */
export const updateParameterThunk = createAsyncThunk<
  any,
  { tableName: string; id: number; payload: ParameterMutationPayload },
  { rejectValue: string }
>(
  'parameters/update',
  async ({ tableName, id, payload }, { rejectWithValue }) => {
    try {
      return await updateParameter(tableName, id, payload);
    } catch (err: any) {
      return rejectWithValue(err?.message || 'Failed to update parameter');
    }
  }
);

/** Fetch checker dashboard list */
export const fetchCheckerList = createAsyncThunk<
  CheckerListResponse,
  CheckerListParams,
  { rejectValue: string }
>('parameters/fetchCheckerList', async (params, { rejectWithValue }) => {
  try {
    return await getCheckerParameterList(params);
  } catch (err: any) {
    return rejectWithValue(err?.message || 'Failed to fetch checker list');
  }
});

/** Perform checker action (approve/reject) */
export const performCheckerActionThunk = createAsyncThunk<
  any,
  CheckerActionPayload,
  { rejectValue: string }
>('parameters/checkerAction', async (payload, { rejectWithValue }) => {
  try {
    return await performCheckerAction(payload);
  } catch (err: any) {
    return rejectWithValue(err?.message || 'Checker action failed');
  }
});

/** Revise a rejected parameter */
export const reviseParameterThunk = createAsyncThunk<
  any,
  { txnId: string; payload: RevisionPayload },
  { rejectValue: string }
>('parameters/revise', async ({ txnId, payload }, { rejectWithValue }) => {
  try {
    return await reviseParameter(txnId, payload);
  } catch (err: any) {
    return rejectWithValue(err?.message || 'Revision failed');
  }
});

/** Fetch parameter counts */
export const fetchParameterCounts = createAsyncThunk<
  ParameterCountsResponse,
  string,
  { rejectValue: string }
>('parameters/fetchCounts', async (workflowStatusIds, { rejectWithValue }) => {
  try {
    return await getParameterCounts(workflowStatusIds);
  } catch (err: any) {
    return rejectWithValue(err?.message || 'Failed to fetch counts');
  }
});

export interface ParameterStatusCounts {
  pendingChecker: ParameterCountsResponse;
  makerInProgress: ParameterCountsResponse;
}

/**
 * Fetch parameter counts for the checker side and the maker side separately.
 * The dashboard's status switch labels each option with its own count, and
 * the module tabs show the sum of both.
 */
export const fetchParameterStatusCounts = createAsyncThunk<
  ParameterStatusCounts,
  { pendingCheckerStatusIds: string; makerInProgressStatusIds: string },
  { rejectValue: string }
>(
  'parameters/fetchStatusCounts',
  async (
    { pendingCheckerStatusIds, makerInProgressStatusIds },
    { rejectWithValue }
  ) => {
    try {
      const [pendingChecker, makerInProgress] = await Promise.all([
        getParameterCounts(pendingCheckerStatusIds),
        getParameterCounts(makerInProgressStatusIds),
      ]);
      return { pendingChecker, makerInProgress };
    } catch (err: any) {
      return rejectWithValue(err?.message || 'Failed to fetch counts');
    }
  }
);

/** Per-module sum of the checker-side and maker-side counts. */
const sumStatusCounts = ({
  pendingChecker,
  makerInProgress,
}: ParameterStatusCounts): ParameterCountsResponse => {
  const total: ParameterCountsResponse = {};
  for (const [tableName, count] of Object.entries(pendingChecker)) {
    total[tableName] = (total[tableName] ?? 0) + count;
  }
  for (const [tableName, count] of Object.entries(makerInProgress)) {
    total[tableName] = (total[tableName] ?? 0) + count;
  }
  return total;
};

// ============================================================================
// SLICE
// ============================================================================

const parametersSlice = createSlice({
  name: 'parameters',
  initialState,
  reducers: {
    clearParameters(state) {
      state.data = {};
    },
    clearParameterByTable(state, action: PayloadAction<string>) {
      delete state.data[action.payload];
    },
    clearSelectedItem(state) {
      state.selectedItem = null;
    },
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: builder => {
    builder
      // === Existing: Available parameters ===
      .addCase(fetchAvailableParameters.pending, state => {
        state.tablesLoading = true;
        state.error = null;
      })
      .addCase(
        fetchAvailableParameters.fulfilled,
        (state, action: PayloadAction<ParameterTable[]>) => {
          state.tablesLoading = false;
          state.availableTables = action.payload;
        }
      )
      .addCase(fetchAvailableParameters.rejected, (state, action) => {
        state.tablesLoading = false;
        state.error = action.payload ?? 'Unknown error';
      })
      // === Data access level parameters ===
      .addCase(fetchDataAccessLevelParameters.pending, state => {
        state.tablesLoading = true;
        state.error = null;
      })
      .addCase(
        fetchDataAccessLevelParameters.fulfilled,
        (state, action: PayloadAction<ParameterTable[]>) => {
          state.tablesLoading = false;
          state.dataAccessLevelTables = action.payload;
        }
      )
      .addCase(fetchDataAccessLevelParameters.rejected, (state, action) => {
        state.tablesLoading = false;
        state.error = action.payload ?? 'Unknown error';
      })

      // === Existing: Parameter values by tables ===
      .addCase(fetchParametersByTables.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchParametersByTables.fulfilled,
        (state, action: PayloadAction<ParametersByTable>) => {
          state.loading = false;
          Object.entries(action.payload).forEach(([table, items]) => {
            state.data[table] = items;
          });
        }
      )
      .addCase(fetchParametersByTables.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Unknown error';
      })

      // === NEW: Fetch Schema ===
      .addCase(fetchParameterSchema.pending, state => {
        state.schemaLoading = true;
        state.error = null;
      })
      .addCase(fetchParameterSchema.fulfilled, (state, action) => {
        state.schemaLoading = false;
        state.schemas[action.payload.tableName] = action.payload;
      })
      .addCase(fetchParameterSchema.rejected, (state, action) => {
        state.schemaLoading = false;
        state.error = action.payload ?? 'Unknown error';
      })

      // === NEW: Fetch List ===
      .addCase(fetchParameterList.pending, state => {
        state.listLoading = true;
        state.error = null;
      })
      .addCase(fetchParameterList.fulfilled, (state, action) => {
        state.listLoading = false;
        state.listData[action.payload.tableName] = action.payload.response;
      })
      .addCase(fetchParameterList.rejected, (state, action) => {
        state.listLoading = false;
        state.error = action.payload ?? 'Unknown error';
      })

      // === NEW: Fetch By ID ===
      .addCase(fetchParameterById.pending, state => {
        state.itemLoading = true;
        state.error = null;
      })
      .addCase(fetchParameterById.fulfilled, (state, action) => {
        state.itemLoading = false;
        state.selectedItem = action.payload.data;
      })
      .addCase(fetchParameterById.rejected, (state, action) => {
        state.itemLoading = false;
        state.error = action.payload ?? 'Unknown error';
      })

      // === NEW: Create ===
      .addCase(createParameterThunk.pending, state => {
        state.creating = true;
        state.error = null;
      })
      .addCase(createParameterThunk.fulfilled, state => {
        state.creating = false;
      })
      .addCase(createParameterThunk.rejected, (state, action) => {
        state.creating = false;
        state.error = action.payload ?? 'Unknown error';
      })

      // === NEW: Update ===
      .addCase(updateParameterThunk.pending, state => {
        state.updating = true;
        state.error = null;
      })
      .addCase(updateParameterThunk.fulfilled, state => {
        state.updating = false;
      })
      .addCase(updateParameterThunk.rejected, (state, action) => {
        state.updating = false;
        state.error = action.payload ?? 'Unknown error';
      })

      // === NEW: Checker List ===
      .addCase(fetchCheckerList.pending, state => {
        state.checkerLoading = true;
        state.error = null;
      })
      .addCase(fetchCheckerList.fulfilled, (state, action) => {
        state.checkerLoading = false;
        state.checkerList = action.payload;
      })
      .addCase(fetchCheckerList.rejected, (state, action) => {
        state.checkerLoading = false;
        state.error = action.payload ?? 'Unknown error';
      })

      // === NEW: Checker Action ===
      .addCase(performCheckerActionThunk.pending, state => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(performCheckerActionThunk.fulfilled, state => {
        state.actionLoading = false;
      })
      .addCase(performCheckerActionThunk.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload ?? 'Unknown error';
      })

      // === NEW: Revision ===
      .addCase(reviseParameterThunk.pending, state => {
        state.updating = true;
        state.error = null;
      })
      .addCase(reviseParameterThunk.fulfilled, state => {
        state.updating = false;
      })
      .addCase(reviseParameterThunk.rejected, (state, action) => {
        state.updating = false;
        state.error = action.payload ?? 'Unknown error';
      })

      // === NEW: Parameter Counts ===
      .addCase(fetchParameterCounts.pending, state => {
        state.countsLoading = true;
        state.error = null;
      })
      .addCase(fetchParameterCounts.fulfilled, (state, action) => {
        state.countsLoading = false;
        state.counts = action.payload;
      })
      .addCase(fetchParameterCounts.rejected, (state, action) => {
        state.countsLoading = false;
        state.error = action.payload ?? 'Unknown error';
      })
      .addCase(fetchParameterStatusCounts.pending, state => {
        state.countsLoading = true;
        state.error = null;
      })
      .addCase(fetchParameterStatusCounts.fulfilled, (state, action) => {
        state.countsLoading = false;
        state.statusCounts = action.payload;
        state.counts = sumStatusCounts(action.payload);
      })
      .addCase(fetchParameterStatusCounts.rejected, (state, action) => {
        state.countsLoading = false;
        state.error = action.payload ?? 'Unknown error';
      });
  },
});

export const {
  clearParameters,
  clearParameterByTable,
  clearSelectedItem,
  clearError,
} = parametersSlice.actions;

export default parametersSlice.reducer;
